import React, { useMemo } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import * as Popover from '@radix-ui/react-popover';
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  ArrowRight,
  Minus,
  Calendar,
  Clock,
  Loader2,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';
import {
  BlackoutCheckResult,
  BlackoutSeverity,
  PolicyValidationConfig,
  LeaveBalanceView,
} from '../../models/attendance';

// --- Types ---

export type EnforcementStatus = 'clear' | 'warning' | 'blocked';

export interface EnforcementItem {
  key: string;
  label: string;
  severity: 'error' | 'warning' | 'info';
  detail?: string;
}

export interface EnforcementPreviewProps
  extends VariantProps<typeof previewVariants> {
  /** Current balance for the selected leave type */
  balance: LeaveBalanceView | null;
  /** Number of days being requested */
  daysRequested: number;
  /** Sandwich days that will also be deducted */
  sandwichDays: number;
  /** Blackout check result (null when not yet checked) */
  blackoutCheck: BlackoutCheckResult | null;
  /** Policy validation config (null when loading) */
  validationConfig: PolicyValidationConfig | null;
  /** Whether form data is being validated (loading state) */
  loading?: boolean;
  /** Whether to show the preview (requires leave type + dates) */
  visible?: boolean;
  /** Days until start date (for notice period check) */
  daysUntilStart?: number | null;
  /** Whether the request exceeds max consecutive days */
  exceedsMaxConsecutive?: boolean;
  className?: string;
}

// --- CVA Variants ---

const previewVariants = cva('rounded-lg border text-sm transition-colors', {
  variants: {
    variant: {
      card: 'p-4',
      popover: '',
    },
  },
  defaultVariants: {
    variant: 'card',
  },
});

const statusVariants = cva('rounded-lg border p-4', {
  variants: {
    status: {
      clear:
        'bg-emerald-50 border-emerald-200 text-emerald-800',
      warning:
        'bg-amber-50 border-amber-200 text-amber-800',
      blocked:
        'bg-red-50 border-red-200 text-red-800',
    },
  },
  defaultVariants: {
    status: 'clear',
  },
});

const rowVariants = cva('flex items-center justify-between py-1.5', {
  variants: {
    type: {
      debit: 'text-red-600',
      credit: 'text-foreground',
      result: 'font-semibold text-foreground border-t border-border pt-2 mt-1',
    },
  },
  defaultVariants: {
    type: 'credit',
  },
});

// --- Skeleton ---

function PreviewSkeleton() {
  return (
    <div className="space-y-3 animate-pulse" data-testid="enforcement-preview-skeleton">
      <div className="flex items-center gap-2">
        <div className="h-5 w-5 rounded-full bg-muted" />
        <div className="h-4 w-32 rounded bg-muted" />
      </div>
      <div className="space-y-2">
        <div className="flex justify-between">
          <div className="h-3 w-24 rounded bg-muted" />
          <div className="h-3 w-12 rounded bg-muted" />
        </div>
        <div className="flex justify-between">
          <div className="h-3 w-28 rounded bg-muted" />
          <div className="h-3 w-12 rounded bg-muted" />
        </div>
        <div className="flex justify-between border-t border-border pt-2 mt-1">
          <div className="h-3.5 w-20 rounded bg-muted" />
          <div className="h-3.5 w-14 rounded bg-muted" />
        </div>
      </div>
    </div>
  );
}

// --- Helpers ---

function resolveStatus(
  blackoutCheck: BlackoutCheckResult | null,
  exceedsMaxConsecutive: boolean,
  balanceAfter: number | null,
  balanceFloor: number | undefined
): EnforcementStatus {
  // Blocked if strict blackout or exceeds max consecutive
  if (blackoutCheck?.isBlocked || exceedsMaxConsecutive) return 'blocked';

  // Blocked if balance drops below floor
  if (balanceAfter != null && balanceFloor != null && balanceAfter < balanceFloor) return 'blocked';

  // Blocked if insufficient balance
  if (balanceAfter != null && balanceAfter < 0) return 'blocked';

  // Warning if advisory blackout, insufficient notice, etc.
  if (blackoutCheck?.severity === BlackoutSeverity.Advisory) return 'warning';
  if (blackoutCheck?.severity === BlackoutSeverity.Conditional) return 'warning';

  return 'clear';
}

function StatusIcon({ status, size = 20 }: { status: EnforcementStatus; size?: number }) {
  switch (status) {
    case 'blocked':
      return <ShieldAlert size={size} />;
    case 'warning':
      return <AlertTriangle size={size} />;
    case 'clear':
      return <ShieldCheck size={size} />;
  }
}

// --- Preview Content ---

function PreviewContent({
  balance,
  daysRequested,
  sandwichDays,
  blackoutCheck,
  validationConfig,
  daysUntilStart,
  exceedsMaxConsecutive = false,
  loading = false,
}: Omit<EnforcementPreviewProps, 'variant' | 'visible' | 'className'>) {
  const { t } = useTranslation(['leaves']);

  const totalDeduction = daysRequested + sandwichDays;
  const balanceBefore = balance?.balance ?? null;
  const rawBalanceAfter = balanceBefore != null ? balanceBefore - totalDeduction : null;
  // Round to 2 decimals to avoid floating-point artifacts (e.g. 2.92 - 3 = -0.08000000000000007)
  const balanceAfter = rawBalanceAfter != null ? Math.round(rawBalanceAfter * 100) / 100 : null;
  const balanceFloor = validationConfig?.balanceFloor;

  const status = resolveStatus(
    blackoutCheck,
    exceedsMaxConsecutive,
    balanceAfter,
    balanceFloor
  );

  const enforcementItems = useMemo(() => {
    const items: EnforcementItem[] = [];

    // Blackout conflict
    if (blackoutCheck?.severity) {
      items.push({
        key: 'blackout',
        label:
          blackoutCheck.isBlocked
            ? t('leaves:enforcementBlackoutBlocked', { name: blackoutCheck.blackoutName })
            : t('leaves:enforcementBlackoutAdvisory', { name: blackoutCheck.blackoutName }),
        severity: blackoutCheck.isBlocked ? 'error' : 'warning',
        detail: blackoutCheck.reason ?? undefined,
      });
    }

    // Max consecutive exceeded
    if (exceedsMaxConsecutive && validationConfig?.maxConsecutiveDays != null) {
      items.push({
        key: 'maxConsecutive',
        label: t('leaves:enforcementMaxConsecutive', { max: validationConfig.maxConsecutiveDays }),
        severity: 'error',
      });
    }

    // Insufficient balance
    if (balanceAfter != null && balanceAfter < 0) {
      items.push({
        key: 'insufficientBalance',
        label: t('leaves:enforcementInsufficientBalance'),
        severity: 'error',
        detail: t('leaves:enforcementInsufficientDetail', {
          available: balanceBefore,
          requested: totalDeduction,
        }),
      });
    }

    // Balance floor
    if (
      balanceAfter != null &&
      balanceAfter >= 0 &&
      balanceFloor != null &&
      balanceAfter < balanceFloor
    ) {
      items.push({
        key: 'balanceFloor',
        label: t('leaves:enforcementBalanceFloor', { floor: balanceFloor }),
        severity: 'error',
        detail: t('leaves:enforcementBalanceFloorDetail', { after: balanceAfter, floor: balanceFloor }),
      });
    }

    // Notice period
    if (
      validationConfig?.minNoticeDays &&
      validationConfig.minNoticeDays > 0 &&
      daysUntilStart != null &&
      daysUntilStart < validationConfig.minNoticeDays
    ) {
      items.push({
        key: 'noticePeriod',
        label: t('leaves:enforcementNoticePeriod', {
          required: validationConfig.minNoticeDays,
          provided: daysUntilStart,
        }),
        severity: 'warning',
      });
    }

    return items;
  }, [
    blackoutCheck,
    exceedsMaxConsecutive,
    validationConfig,
    balanceAfter,
    balanceBefore,
    balanceFloor,
    totalDeduction,
    daysUntilStart,
    t,
  ]);

  if (loading) return <PreviewSkeleton />;

  // No impact — minimized green state
  if (status === 'clear' && enforcementItems.length === 0) {
    return (
      <div
        className={statusVariants({ status: 'clear' })}
        role="status"
        aria-label={t('leaves:enforcementNoConflicts')}
      >
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} className="text-emerald-600 shrink-0" />
          <span className="font-medium">{t('leaves:enforcementNoConflicts')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Status header */}
      <div className={statusVariants({ status })}>
        <div className="flex items-center gap-2">
          <StatusIcon status={status} size={18} />
          <span className="font-medium">
            {status === 'blocked'
              ? t('leaves:enforcementBlocked')
              : status === 'warning'
                ? t('leaves:enforcementWarnings')
                : t('leaves:enforcementNoConflicts')}
          </span>
        </div>
      </div>

      {/* Balance impact ledger */}
      {balanceBefore != null && (
        <div className="space-y-0.5 px-1">
          <div className={rowVariants({ type: 'credit' })}>
            <span className="text-muted-foreground">{t('leaves:enforcementBalanceBefore')}</span>
            <span className="tabular-nums font-medium">
              {balanceBefore} {t('days')}
            </span>
          </div>

          <div className={rowVariants({ type: 'debit' })}>
            <span className="flex items-center gap-1.5">
              <Minus size={12} className="shrink-0" />
              {t('leaves:enforcementDaysRequested')}
            </span>
            <span className="tabular-nums">
              {daysRequested} {t('days')}
            </span>
          </div>

          {sandwichDays > 0 && (
            <div className={rowVariants({ type: 'debit' })}>
              <span className="flex items-center gap-1.5">
                <Calendar size={12} className="shrink-0" />
                {t('leaves:enforcementSandwichDays')}
              </span>
              <span className="tabular-nums">
                {sandwichDays} {t('days')}
              </span>
            </div>
          )}

          <div className={rowVariants({ type: 'result' })}>
            <span className="flex items-center gap-1.5">
              <ArrowRight size={14} className="shrink-0 rtl:rotate-180" />
              {t('leaves:enforcementBalanceAfter')}
            </span>
            <span
              className={cn(
                'tabular-nums',
                balanceAfter != null && balanceAfter < 0
                  ? 'text-red-600'
                  : balanceAfter != null &&
                      balanceFloor != null &&
                      balanceAfter < balanceFloor
                    ? 'text-amber-600'
                    : 'text-emerald-600'
              )}
            >
              {balanceAfter} {t('days')}
            </span>
          </div>
        </div>
      )}

      {/* Enforcement rule items */}
      {enforcementItems.length > 0 && (
        <div className="space-y-2">
          {enforcementItems.map((item) => (
            <div
              key={item.key}
              className={cn(
                'flex items-start gap-2 px-2 py-1.5 rounded text-xs',
                item.severity === 'error'
                  ? 'text-red-700'
                  : item.severity === 'warning'
                    ? 'text-amber-700'
                    : 'text-blue-700'
              )}
            >
              {item.severity === 'error' ? (
                <ShieldAlert size={14} className="shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              )}
              <div>
                <p className="font-medium">{item.label}</p>
                {item.detail && <p className="opacity-85 mt-0.5">{item.detail}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Main Component ---

export function EnforcementPreview({
  variant = 'card',
  visible = true,
  className,
  ...contentProps
}: EnforcementPreviewProps) {
  const { t } = useTranslation(['leaves']);
  const resolvedVariant = variant ?? 'card';

  if (!visible) return null;

  if (resolvedVariant === 'popover') {
    const totalDeduction = contentProps.daysRequested + contentProps.sandwichDays;
    const balanceBefore = contentProps.balance?.balance ?? null;
    const rawBalanceAfter = balanceBefore != null ? balanceBefore - totalDeduction : null;
    const balanceAfter = rawBalanceAfter != null ? Math.round(rawBalanceAfter * 100) / 100 : null;

    const status = resolveStatus(
      contentProps.blackoutCheck,
      contentProps.exceedsMaxConsecutive ?? false,
      balanceAfter,
      contentProps.validationConfig?.balanceFloor
    );

    return (
      <Popover.Root>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
              status === 'blocked'
                ? 'bg-red-100 text-black hover:bg-red-200'
                : status === 'warning'
                  ? 'bg-amber-100 text-black hover:bg-amber-200'
                  : 'bg-emerald-100 text-black hover:bg-emerald-200',
              className
            )}
            data-testid="enforcement-preview-trigger"
            aria-label={t('leaves:enforcementPreviewLabel')}
          >
            <StatusIcon status={status} size={14} />
            {status === 'blocked'
              ? t('leaves:enforcementBlocked')
              : status === 'warning'
                ? t('leaves:enforcementWarnings')
                : t('leaves:enforcementNoConflicts')}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="z-50 w-80 rounded-lg border border-border bg-card p-4 shadow-lg outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2"
            sideOffset={8}
            data-testid="enforcement-preview"
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-foreground">
                {t('leaves:enforcementPreviewTitle')}
              </h4>
              <Popover.Close
                className="text-muted-foreground hover:text-foreground rounded-sm"
                aria-label={t('common:Close')}
                data-testid="enforcement-preview-close"
              >
                <X size={14} />
              </Popover.Close>
            </div>
            <PreviewContent {...contentProps} />
            <Popover.Arrow className="fill-border" />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    );
  }

  // Card variant (default) — inline in form
  return (
    <div
      className={cn(previewVariants({ variant: 'card' }), 'border-border bg-card', className)}
      data-testid="enforcement-preview"
      role="region"
      aria-label={t('leaves:enforcementPreviewLabel')}
    >
      <div className="flex items-center gap-2 mb-3">
        <Shield size={16} className="text-muted-foreground shrink-0" />
        <h4 className="text-sm font-semibold text-foreground">
          {t('leaves:enforcementPreviewTitle')}
        </h4>
      </div>
      <PreviewContent {...contentProps} />
    </div>
  );
}

export default EnforcementPreview;
